import { VertoBye, VertoInfo, VertoInvite, BaseComponent, selectors, VertoModify, componentSelectors, actions, isSATAuth, 
// VertoAttach,
VertoAnswer, asyncRetry, constDelay, SYMBOL_EXECUTE_CONNECTION_CLOSED, SYMBOL_EXECUTE_TIMEOUT, } from '@signalwire/core';
import RTCPeer from './RTCPeer';
import { stopTrack, getUserMedia, streamIsValid } from './utils';
import { hasMatchingSdpDirection, sdpRemoveLocalCandidates, } from './utils/sdpHelpers';
import * as workers from './workers';
import { AUDIO_CONSTRAINTS, AUDIO_CONSTRAINTS_SCREENSHARE, DEFAULT_CALL_OPTIONS, INVITE_VERSION, VIDEO_CONSTRAINTS, } from './utils/constants';
export class BaseConnection extends BaseComponent {
    constructor(options) {
        super(options);
        /** @internal */
        this.leaveReason = undefined;
        /** @internal */
        this.gotEarly = false;
        this.state = 'new';
        this.prevState = 'new';
        this.rtcPeerMap = new Map();
        this.resuming = false;
        this._myWorkers = [];
        /** @internal */
        this.onVertoBye = (params) => {
            this.logger.debug('onVertoBye', params);
            const { rtcPeerId, byeCause = 'NORMAL_CLEARING', byeCauseCode = '16', redirectDestination, } = params;
            this.cause = String(byeCause);
            this.causeCode = String(byeCauseCode);
            const rtcPeer = this.getRTCPeerById(rtcPeerId);
            if (!rtcPeer) {
                return this.logger.warn('Invalid RTCPeer to hangup', params);
            }
            if (redirectDestination && rtcPeer.localSdp) {
                this.logger.debug('Redirect Destination to:', redirectDestination, 'for RTCPeer:', rtcPeer.uuid);
                // Force nodeId to redirectDestination
                this.executeInvite(rtcPeer.localSdp, rtcPeer.uuid, redirectDestination);
                return;
            }
            // Notify RTCPeer for the bad signaling error
            rtcPeer.onRemoteBye({ code: this.causeCode, message: this.cause });
            // Set state to hangup only if the rtcPeer is the current one
            if (this.activeRTCPeerId === (rtcPeer === null || rtcPeer === void 0 ? void 0 : rtcPeer.uuid)) {
                this.logger.debug('onVertoBye go hangup');
                this.setState('hangup');
            }
        };
        this.options = Object.assign(Object.assign({}, DEFAULT_CALL_OPTIONS), options);
        this._checkDefaultMediaConstraints();
        this.setState('new');
        this.logger.trace('New Call with Options:', this.options);
        this._initPeer();
    }
    get id() {
        return this.__uuid;
    }
    get active() {
        return this.state === 'active';
    }
    get requesting() {
        return this.state === 'requesting';
    }
    get trying() {
        return this.state === 'trying';
    }
    get memberId() {
        // @ts-expect-error
        return this.component.memberId;
    }
    get previewUrl() {
        // @ts-expect-error
        return this.component.previewUrl;
    }
    get roomId() {
        // @ts-expect-error
        return this.component.roomId;
    }
    get roomSessionId() {
        // @ts-expect-error
        return this.component.roomSessionId;
    }
    get nodeId() {
        // @ts-expect-error
        return this.component.nodeId || this.options.nodeId;
    }
    get callId() {
        var _a;
        return ((_a = this.peer) === null || _a === void 0 ? void 0 : _a.uuid) || '';
    }
    get localStream() {
        var _a;
        return (_a = this.peer) === null || _a === void 0 ? void 0 : _a.localStream;
    }
    set localStream(stream) {
        if (this.peer) {
            this.peer.localStream = stream;
        }
    }
    get remoteStream() {
        var _a;
        return (_a = this.peer) === null || _a === void 0 ? void 0 : _a.remoteStream;
    }
    get iceServers() {
        var _a, _b;
        return (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.iceServers) !== null && _b !== void 0 ? _b : this.select(selectors.getIceServers);
    }
    get component() {
        return (this.select((state) => componentSelectors.getComponent(state, this.callId)) || {});
    }
    get cameraId() {
        return this.peer ? this.peer.getDeviceId('video') : null;
    }
    get cameraLabel() {
        return this.peer ? this.peer.getDeviceLabel('video') : null;
    }
    get cameraConstraints() {
        return this.peer ? this.peer.getTrackConstraints('video') : null;
    }
    get microphoneId() {
        return this.peer ? this.peer.getDeviceId('audio') : null;
    }
    get microphoneLabel() {
        return this.peer ? this.peer.getDeviceLabel('audio') : null;
    }
    get microphoneConstraints() {
        return this.peer ? this.peer.getTrackConstraints('audio') : null;
    }
    get withAudio() {
        var _a;
        return Boolean((_a = this.peer) === null || _a === void 0 ? void 0 : _a.hasAudioReceiver);
    }
    get withVideo() {
        var _a;
        return Boolean((_a = this.peer) === null || _a === void 0 ? void 0 : _a.hasVideoReceiver);
    }
    get localAudioTrack() {
        return this.peer ? this.peer.localAudioTrack : null;
    }
    get localVideoTrack() {
        return this.peer ? this.peer.localVideoTrack : null;
    }
    get peer() {
        return this.getRTCPeerById(this.activeRTCPeerId);
    }
    set peer(rtcPeer) {
        if (!rtcPeer) {
            this.logger.warn('Invalid RTCPeer', rtcPeer);
            return;
        }
        this.logger.debug('Set RTCPeer', rtcPeer.uuid, rtcPeer);
        this.rtcPeerMap.set(rtcPeer.uuid, rtcPeer);
        if (this.peer && this.peer.instance && this.callId !== rtcPeer.uuid) {
            const oldPeerId = this.peer.uuid;
            this.logger.debug('>>> Stop old RTCPeer', oldPeerId);
            // Hangup the previous RTCPeer
            this.hangup(oldPeerId).catch(console.error);
            this.peer.detachAndStop();
            // Remove RTCPeer from local cache to stop answering to ping/pong
            // this.rtcPeerMap.delete(oldPeerId)
        }
        this.logger.debug('>>> Replace RTCPeer with', rtcPeer.uuid);
        this.activeRTCPeerId = rtcPeer.uuid;
    }
    // Implementation for the overloaded emit
    emit(event, ...args) {
        return super.emit(event, ...args);
    }
    dialogParams(rtcPeerId) {
        const { destinationNumber, attach, callerName, callerNumber, remoteCallerName, remoteCallerNumber, userVariables, screenShare, additionalDevice, fromCallAddressId, pingSupported = true, } = this.options;
        return {
            dialogParams: {
                id: rtcPeerId,
                destinationNumber,
                attach,
                reattaching: attach,
                callerName,
                callerNumber,
                remoteCallerName,
                remoteCallerNumber,
                userVariables,
                screenShare,
                additionalDevice,
                fromCallAddressId,
                pingSupported,
                version: INVITE_VERSION,
            },
        };
    }
    getRTCPeerById(rtcPeerId) {
        return this.rtcPeerMap.get(rtcPeerId);
    }
    appendRTCPeer(rtcPeer) {
        return this.rtcPeerMap.set(rtcPeer.uuid, rtcPeer);
    }
    removeRTCPeer(rtcPeerId) {
        return this.rtcPeerMap.delete(rtcPeerId);
    }
    setActiveRTCPeer(rtcPeerId) {
        this.peer = this.getRTCPeerById(rtcPeerId);
    }
    setLocalStream(stream) {
        return new Promise(async (resolve, reject) => {
            try {
                if (!this.peer || !this.localStream) {
                    return reject(new Error('Invalid RTCPeerConnection.'));
                }
                if (!streamIsValid(stream)) {
                    return reject(new Error('Invalid stream provided.'));
                }
                const prevAudioTracks = this.localStream.getAudioTracks();
                const newAudioTracks = stream.getAudioTracks();
                if (newAudioTracks.length <= 0) {
                    this.logger.info('No audio track found in the stream provided. Audio will be unaffected.');
                }
                else {
                    prevAudioTracks.forEach((track) => {
                        var _a;
                        stopTrack(track);
                        (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.removeTrack(track);
                    });
                    newAudioTracks.forEach((track) => {
                        var _a;
                        (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.addTrack(track);
                    });
                }
                const prevVideoTracks = this.localStream.getVideoTracks();
                const newVideoTracks = stream.getVideoTracks();
                if (newVideoTracks.length <= 0) {
                    this.logger.info('No video track found in the stream provided. Video will be unaffected.');
                }
                else {
                    prevVideoTracks.forEach((track) => {
                        var _a;
                        stopTrack(track);
                        (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.removeTrack(track);
                    });
                    newVideoTracks.forEach((track) => {
                        var _a;
                        (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.addTrack(track);
                    });
                }
                await this.updateStream(this.localStream);
                this.logger.debug('setLocalStream done');
                resolve(this.localStream);
            }
            catch (error) {
                this.logger.error('setLocalStream', error);
                reject(error);
            }
        });
    }
    /**
     * Verto messages have to be wrapped into an execute
     * request and sent using the proper RPC WebRTCMethod.
     */
    vertoExecute(params) {
        return this.execute({
            method: this._getRPCMethod(),
            params,
        });
    }
    /** @internal */
    _getRPCMethod() {
        const authorization = this.select(selectors.getAuthorization);
        if (authorization && isSATAuth(authorization)) {
            return 'webrtc.verto';
        }
        return 'video.message';
    }
    /** @internal */
    async _triggerNewRTCPeer() {
        this.logger.debug('_triggerNewRTCPeer Start');
        try {
            this.logger.debug('Build a new RTCPeer');
            const rtcPeer = this._buildPeer('offer');
            this.logger.debug('Trigger start for the new RTCPeer!');
            await rtcPeer.start();
        }
        catch (error) {
            this.logger.error('Error building new RTCPeer to promote/demote', error);
        }
    }
    updateCamera(constraints) {
        return this.applyConstraintsAndRefreshStream({
            video: Object.assign({ aspectRatio: 16 / 9 }, constraints),
        });
    }
    updateMicrophone(constraints) {
        return this.applyConstraintsAndRefreshStream({
            audio: constraints,
        });
    }
    /**
     * Determines the appropriate {@link RTCRtpTransceiverDirection} based on current audio/video
     * and negotiation options. The returned direction tells the peer connection
     * whether to send, receive, both, or remain inactive for the given media kind.
     */
    _getTransceiverDirection(kind) {
        let direction = 'inactive';
        if (kind === 'audio') {
            if (this.options.audio && this.options.negotiateAudio) {
                direction = 'sendrecv';
            }
            else if (this.options.audio && !this.options.negotiateAudio) {
                direction = 'sendonly';
            }
            else if (!this.options.audio && this.options.negotiateAudio) {
                direction = 'recvonly';
            }
            else {
                direction = 'inactive';
            }
        }
        if (kind === 'video') {
            if (this.options.video && this.options.negotiateVideo) {
                direction = 'sendrecv';
            }
            else if (this.options.video && !this.options.negotiateVideo) {
                direction = 'sendonly';
            }
            else if (!this.options.video && this.options.negotiateVideo) {
                direction = 'recvonly';
            }
            else {
                direction = 'inactive';
            }
        }
        return direction;
    }
    /**
     * Adjusts senders based on the given audio/video constraints. If a constraint is set to false,
     * it stops the corresponding outbound track. Returns true if at least one sender is active,
     * otherwise false.
     */
    manageSendersWithConstraints(constraints) {
        if (constraints.audio === false) {
            this.logger.info('Switching off the microphone');
            this.stopOutboundAudio();
        }
        if (constraints.video === false) {
            this.logger.info('Switching off the camera');
            this.stopOutboundVideo();
        }
        return constraints.audio || constraints.video;
    }
    /**
     * Attempts to obtain a new media stream that matches the given constraints, using a recursive
     * strategy. If the new constraints fail, it tries to restore the old constraints.
     * Returns a Promise that resolves to the new MediaStream or resolves without a stream if
     * constraints were fully disabled. Rejects on unrecoverable errors.
     */
    updateConstraints(constraints, { attempt = 0 } = {}) {
        if (attempt > 1) {
            return Promise.reject(new Error('Failed to update constraints'));
        }
        return new Promise(async (resolve, reject) => {
            var _a;
            try {
                if (!this.peer) {
                    return reject(new Error('Invalid RTCPeerConnection.'));
                }
                if (!Object.keys(constraints).length) {
                    return reject(new Error('Invalid audio/video constraints.'));
                }
                this.logger.debug('updateConstraints trying constraints', this.__uuid, constraints);
                const shouldContinueWithUpdate = this.manageSendersWithConstraints(constraints);
                if (!shouldContinueWithUpdate) {
                    this.logger.debug('Either `video` and `audio` (or both) constraints were set to `false` so their corresponding senders (if any) were stopped');
                    return resolve();
                }
                /**
                 * On some devices/browsers you cannot open more than one MediaStream at
                 * a time, per process. When this happens we'll try to do the following:
                 * 1. Stop the current media tracks
                 * 2. Try to get new media tracks with the new constraints
                 * 3. If we get an error: restore the media tracks using the previous
                 *    constraints.
                 * @see
                 * https://bugzilla.mozilla.org/show_bug.cgi?id=1238038
                 *
                 * Instead of just replace the track, force-stop the current one to free
                 * up the device
                 */
                let oldConstraints = {};
                (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((track) => {
                    var _a;
                    /**
                     * We'll keep a reference of the original constraints so if something
                     * fails we should be able to restore them.
                     */
                    // @ts-expect-error
                    oldConstraints[track.kind] = track.getConstraints();
                    // @ts-expect-error
                    if (constraints[track.kind] !== undefined) {
                        this.logger.debug('updateConstraints stop old tracks first?');
                        this.logger.debug('Track readyState:', track.kind, track.readyState);
                        stopTrack(track);
                        track.stop();
                        (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.removeTrack(track);
                    }
                });
                let newStream;
                try {
                    this.logger.info('updateConstraints with', constraints);
                    newStream = await getUserMedia(constraints);
                }
                catch (error) {
                    this.logger.error('Error updating device constraints:', error.name, error.message, error);
                    this.logger.info('Restoring previous constraints', oldConstraints);
                    await this.updateConstraints(oldConstraints, {
                        attempt: attempt + 1,
                    });
                    return reject(error);
                }
                this.logger.debug('updateConstraints done');
                resolve(newStream);
            }
            catch (error) {
                this.logger.error('updateConstraints', error);
                reject(error);
            }
        });
    }
    /**
     * Updates local tracks and transceivers from the given stream.
     * For each new track, it adds/updates a transceiver and emits updated device events.
     */
    async updateStream(stream) {
        try {
            if (!this.peer) {
                throw new Error('Invalid RTCPeerConnection.');
            }
            // Store the previous tracks for device.updated events
            const prevVideoTrack = this.localVideoTrack;
            const prevAudioTrack = this.localAudioTrack;
            this.logger.debug('updateStream got stream', stream);
            if (!this.localStream) {
                this.localStream = new MediaStream();
            }
            const tracks = stream.getTracks();
            this.logger.debug(`updateStream got ${tracks.length} tracks`);
            for (const newTrack of tracks) {
                this.logger.debug('updateStream apply track: ', newTrack);
                // Add or update the transceiver (may trigger renegotiation)
                await this.handleTransceiverForTrack(newTrack);
                // Emit the device.updated events
                this.emitDeviceUpdatedEvents({
                    newTrack,
                    prevAudioTrack,
                    prevVideoTrack,
                });
            }
            this.logger.debug('updateStream done');
        }
        catch (error) {
            this.logger.error('updateStream error', error);
            throw error;
        }
    }
    /**
     * Finds or creates a transceiver for the new track. If an existing transceiver is found,
     * replaces its track and updates its direction, if needed. If no transceiver is found,
     * adds a new one. The method can trigger renegotiation.
     */
    async handleTransceiverForTrack(newTrack) {
        var _a, _b;
        if (!this.peer) {
            return this.logger.error('Invalid RTCPeerConnection');
        }
        const transceiver = this.peer.instance
            .getTransceivers()
            .find(({ mid, sender, receiver }) => {
            if (sender.track && sender.track.kind === newTrack.kind) {
                this.logger.debug('Found transceiver by sender');
                return true;
            }
            if (receiver.track && receiver.track.kind === newTrack.kind) {
                this.logger.debug('Found transceiver by receiver');
                return true;
            }
            if (mid === null) {
                this.logger.debug('Found disassociated transceiver');
                return true;
            }
            return false;
        });
        if (transceiver) {
            this.logger.debug('handleTransceiverForTrack got transceiver', transceiver.currentDirection, transceiver.mid);
            // Existing transceiver found, replace track if it's different
            if (((_a = transceiver.sender.track) === null || _a === void 0 ? void 0 : _a.id) !== newTrack.id) {
                await transceiver.sender.replaceTrack(newTrack);
                this.logger.debug(`handleTransceiverForTrack replaceTrack for ${newTrack.kind}`);
            }
            // Update direction if needed
            const newDirection = this._getTransceiverDirection(newTrack.kind);
            if (transceiver.direction !== newDirection) {
                transceiver.direction = newDirection;
                this.logger.debug(`handleTransceiverForTrack set direction to ${newDirection}`);
            }
            // Stop old track and add a new one
            this.replaceOldTrack(newTrack);
        }
        else {
            this.logger.debug('handleTransceiverForTrack no transceiver found; addTransceiver and start dancing!');
            // No suitable transceiver found, add a new one
            const direction = this._getTransceiverDirection(newTrack.kind);
            this.peer.type = 'offer';
            (_b = this.localStream) === null || _b === void 0 ? void 0 : _b.addTrack(newTrack);
            this.peer.instance.addTransceiver(newTrack, {
                direction: direction,
                streams: [this.localStream],
            });
        }
    }
    /**
     * Replaces old tracks of the same kind in the local stream with the new track.
     * Stops and removes the old track, then adds the new one. Also updates related
     * device options and reattaches track listeners.
     */
    replaceOldTrack(newTrack) {
        if (!this.peer || !this.localStream) {
            return this.logger.error('Invalid RTCPeerConnection');
        }
        // Detach listeners because stopTrack will trigger the track ended event
        this.peer._detachAudioTrackListener();
        this.peer._detachVideoTrackListener();
        this.localStream.getTracks().forEach((oldTrack) => {
            var _a;
            if (oldTrack.kind === newTrack.kind && oldTrack.id !== newTrack.id) {
                this.logger.debug('replaceOldTrack stop old track and apply new one');
                stopTrack(oldTrack);
                (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.removeTrack(oldTrack);
            }
        });
        this.localStream.addTrack(newTrack);
        if (newTrack.kind === 'audio') {
            this.options.micId = newTrack.getSettings().deviceId;
        }
        if (newTrack.kind === 'video') {
            this.options.camId = newTrack.getSettings().deviceId;
        }
        // Attach listeners again
        this.peer._attachAudioTrackListener();
        this.peer._attachVideoTrackListener();
    }
    /**
     * Emits device updated events for audio or video. Uses previously stored
     * track references to indicate what changed between old and new devices.
     */
    emitDeviceUpdatedEvents({ newTrack, prevAudioTrack, prevVideoTrack, }) {
        if (newTrack.kind === 'audio') {
            this.emit('microphone.updated', {
                previous: {
                    deviceId: prevAudioTrack === null || prevAudioTrack === void 0 ? void 0 : prevAudioTrack.id,
                    label: prevAudioTrack === null || prevAudioTrack === void 0 ? void 0 : prevAudioTrack.label,
                },
                current: {
                    deviceId: newTrack.id,
                    label: newTrack.label,
                },
            });
        }
        else if (newTrack.kind === 'video') {
            this.emit('camera.updated', {
                previous: {
                    deviceId: prevVideoTrack === null || prevVideoTrack === void 0 ? void 0 : prevVideoTrack.id,
                    label: prevVideoTrack === null || prevVideoTrack === void 0 ? void 0 : prevVideoTrack.label,
                },
                current: {
                    deviceId: newTrack.id,
                    label: newTrack.label,
                },
            });
        }
    }
    /**
     * Applies the given constraints by retrieving a new stream and then uses
     * {@link updateStream} to synchronize local tracks with that new stream.
     */
    async applyConstraintsAndRefreshStream(constraints) {
        const newStream = await this.updateConstraints(constraints);
        if (newStream) {
            await this.updateStream(newStream);
        }
    }
    runRTCPeerWorkers(rtcPeerId) {
        const vertoWorker = this.runWorker('vertoEventWorker', {
            worker: workers.vertoEventWorker,
            initialState: { rtcPeerId },
        });
        this._myWorkers.push(vertoWorker);
        const main = !(this.options.additionalDevice || this.options.screenShare);
        if (main) {
            const subscribedWorker = this.runWorker('roomSubscribedWorker', {
                worker: workers.roomSubscribedWorker,
                initialState: { rtcPeerId },
            });
            this._myWorkers.push(subscribedWorker);
            const promoteDemoteWorker = this.runWorker('promoteDemoteWorker', {
                worker: workers.promoteDemoteWorker,
                initialState: { rtcPeerId },
            });
            this._myWorkers.push(promoteDemoteWorker);
        }
    }
    removeRTCWorkers() {
        for (const task of this._myWorkers) {
            this.cancelWorker(task);
        }
        this._myWorkers = [];
    }
    _destroyPeer() {
        if (this.peer) {
            //clean up previous attempt
            this.peer.detachAndStop();
            this.removeRTCWorkers();
            this.removeRTCPeer(this.peer.uuid);
            this.peer = undefined;
        }
    }
    /** @internal */
    invite() {
        return asyncRetry({
            asyncCallable: async () => {
                return new Promise(async (resolve, reject) => {
                    await this._waitUntilSessionAuthorized();
                    this.direction = 'outbound';
                    this.peer = this._buildPeer('offer');
                    try {
                        await this.peer.start();
                        resolve(this);
                    }
                    catch (error) {
                        this.logger.error('Invite error', error);
                        this._destroyPeer();
                        reject(error);
                    }
                });
            },
            maxRetries: 5,
            delayFn: constDelay({ initialDelay: 0 }),
            expectedErrorHandler: (error) => {
                if (this.requesting &&
                    [SYMBOL_EXECUTE_CONNECTION_CLOSED, SYMBOL_EXECUTE_TIMEOUT].includes(error)) {
                    this.logger.debug('Retrying verto.invite with new RTCPeer');
                    return false; // we should retry
                }
                // other case are expected to be handle upstream
                return true;
            },
        });
    }
    /** @internal */
    answer() {
        return new Promise(async (resolve, reject) => {
            this.direction = 'inbound';
            if (!this.peer) {
                this.peer = this._buildPeer('answer');
            }
            try {
                await this.peer.start();
                resolve(this);
            }
            catch (error) {
                this.logger.error('Answer error', error);
                reject(error);
            }
        });
    }
    /** @internal */
    async onLocalSDPReady(rtcPeer) {
        if (!rtcPeer.instance.localDescription) {
            this.logger.error('Missing localDescription', rtcPeer);
            throw new Error('Invalid RTCPeerConnection localDescription');
        }
        const { type, sdp } = rtcPeer.instance.localDescription;
        const mungedSDP = this._mungeSDP(sdp);
        this.logger.debug('LOCAL SDP \n', `Type: ${type}`, '\n\n', mungedSDP);
        switch (type) {
            case 'offer':
                this._watchSessionAuth();
                // If we have a remoteDescription already, send reinvite
                if (!this.resuming && rtcPeer.instance.remoteDescription) {
                    return this.executeUpdateMedia(mungedSDP, rtcPeer.uuid);
                }
                else {
                    return this.executeInvite(mungedSDP, rtcPeer.uuid);
                }
            case 'answer':
                return this.executeAnswer(mungedSDP, rtcPeer.uuid);
            default:
                return this.logger.error(`Unknown SDP type: '${type}' on call ${this.id}`);
        }
    }
    /** @internal */
    _closeWSConnection() {
        this._watchSessionAuth();
        this.store.dispatch(actions.sessionForceCloseAction());
    }
    _watchSessionAuth() {
        if (this.sessionAuthTask) {
            this.sessionAuthTask.cancel();
        }
        this.sessionAuthTask = this.runWorker('sessionAuthWorker', {
            worker: workers.sessionAuthWorker,
        });
    }
    /** @internal */
    async resume() {
        var _a;
        this.logger.warn(`[resume] Call ${this.id}`);
        if ((_a = this.peer) === null || _a === void 0 ? void 0 : _a.instance) {
            const { connectionState } = this.peer.instance;
            this.logger.debug(`[resume] connectionState for ${this.id} is '${connectionState}'`);
            if (connectionState !== 'closed') {
                this.resuming = true;
                this.peer.restartIce();
            }
        }
    }
    /**
     * Send the `verto.invite` only if the state is either `new` or `requesting`
     *   - new: the first time we send out the offer.
     *   - requesting: we received a redirectDestination so need to send it again
     *     specifying nodeId.
     *
     */
    async executeInvite(sdp, rtcPeerId, nodeId) {
        const rtcPeer = this.getRTCPeerById(rtcPeerId);
        if (!rtcPeer || (rtcPeer.instance.remoteDescription && !this.resuming)) {
            throw new Error(`RTCPeer '${rtcPeerId}' already has a remoteDescription. Invalid invite.`);
        }
        // Set state to `requesting` only when `new`, otherwise keep it as `requesting`.
        if (this.state === 'new') {
            this.setState('requesting');
        }
        try {
            const ssOpts = this.options.screenShare
                ? {
                    layout: this.options.layout,
                    positions: this.options.positions,
                }
                : {};
            const message = VertoInvite(Object.assign(Object.assign(Object.assign({}, this.dialogParams(rtcPeerId)), ssOpts), { sdp }));
            let subscribe = [];
            if (this.options.screenShare) {
                /** @ts-expect-error - Only being used for debugging purposes */
                subscribe = ['video.room.screenshare'];
            }
            else if (this.options.additionalDevice) {
                /** @ts-expect-error - Only being used for debugging purposes */
                subscribe = ['video.room.additionaldevice'];
            }
            else {
                subscribe = this.getSubscriptions();
            }
            this.logger.debug('Subscribing to', subscribe);
            const response = await this.vertoExecute({
                message,
                callID: rtcPeerId,
                node_id: nodeId !== null && nodeId !== void 0 ? nodeId : this.options.nodeId,
                subscribe,
            });
            if (this.state === 'requesting') {
                // The Server Created the call, and now is trying to connect us to the destination
                this.setState('trying');
            }
            this.logger.debug('Invite response', response);
            this.resuming = false;
        }
        catch (error) {
            this.setState('hangup');
            throw error;
        }
    }
    /**
     * Send the `verto.answer` only if the state is `new`
     *   - new: the first time we send out the answer.
     */
    async executeAnswer(sdp, rtcPeerId, nodeId) {
        // Set state to `answering` only when `new`, otherwise keep it as `answering`.
        if (this.state === 'new') {
            this.setState('answering');
        }
        try {
            const message = VertoAnswer(Object.assign(Object.assign({}, this.dialogParams(rtcPeerId)), { sdp }));
            const response = await this.vertoExecute({
                message,
                callID: rtcPeerId,
                node_id: nodeId !== null && nodeId !== void 0 ? nodeId : this.options.nodeId,
                subscribe: this.getSubscriptions(),
            });
            this.logger.debug('Answer response', response);
            this.resuming = false;
            /** Call is active so set the RTCPeer */
            this.setActiveRTCPeer(rtcPeerId);
        }
        catch (error) {
            this.setState('hangup');
            throw error;
        }
    }
    /**
     * Send the `verto.modify` when it's an offer and remote is already present
     * It helps in renegotiation.
     */
    async executeUpdateMedia(sdp, rtcPeerId) {
        var _a, _b;
        try {
            const message = VertoModify(Object.assign(Object.assign({}, this.dialogParams(rtcPeerId)), { sdp, action: 'updateMedia' }));
            const response = await this.vertoExecute({
                message,
                callID: rtcPeerId,
                node_id: this.nodeId,
            });
            this.logger.debug('UpdateMedia response', response);
            if (!this.peer) {
                return this.logger.error('Invalid RTCPeer to updateMedia');
            }
            if (!response.sdp) {
                return this.logger.error('UpdateMedia invalid SDP answer', response);
            }
            await this.peer.onRemoteSdp(response.sdp);
        }
        catch (error) {
            // Should we hangup when renegotiation fails?
            this.logger.error('UpdateMedia error', error);
            // Reject the pending negotiation promise
            (_b = (_a = this.peer) === null || _a === void 0 ? void 0 : _a._pendingNegotiationPromise) === null || _b === void 0 ? void 0 : _b.reject(error);
            throw error;
        }
    }
    async hangup(id) {
        const rtcPeerId = id !== null && id !== void 0 ? id : this.callId;
        if (!rtcPeerId) {
            throw new Error('Invalid RTCPeer ID to hangup');
        }
        try {
            const message = VertoBye(this.dialogParams(rtcPeerId));
            await this.vertoExecute({
                message,
                callID: rtcPeerId,
                node_id: this.nodeId,
            });
        }
        catch (error) {
            this.logger.error('Hangup error:', error);
        }
        finally {
            if (rtcPeerId !== this.callId) {
                return this.logger.warn('Prevent setState hangup', rtcPeerId, this.callId);
            }
            this.setState('hangup');
        }
    }
    async hangupAll() {
        const rtcPeerId = this.callId;
        if (!rtcPeerId) {
            throw new Error('Invalid RTCPeer ID to hangup');
        }
        try {
            const message = VertoBye(Object.assign({ cause: 'REJECT_ALL', causeCode: '825' }, this.dialogParams(rtcPeerId)));
            await this.vertoExecute({
                message,
                callID: rtcPeerId,
                node_id: this.nodeId,
            });
        }
        catch (error) {
            this.logger.error('HangupAll error:', error);
        }
        finally {
            this.setState('hangup');
        }
    }
    async sendDigits(dtmf) {
        const rtcPeerId = this.callId;
        if (!rtcPeerId) {
            throw new Error('Invalid RTCPeer ID to send DTMF');
        }
        const message = VertoInfo(Object.assign(Object.assign({}, this.dialogParams(rtcPeerId)), { dtmf }));
        await this.vertoExecute({
            message,
            callID: rtcPeerId,
            node_id: this.nodeId,
        });
    }
    /** @internal */
    doReinviteWithRelayOnly() {
        if (this.peer && this.active) {
            this.peer.restartIceWithRelayOnly();
        }
    }
    /** @internal */
    stopOutboundAudio() {
        if (this.peer && this.active) {
            this.peer.stopTrackSender('audio');
        }
    }
    /** @internal */
    restoreOutboundAudio() {
        if (this.peer && this.active) {
            this.peer.restoreTrackSender('audio');
        }
    }
    /** @internal */
    stopOutboundVideo() {
        if (this.peer && this.active) {
            this.peer.stopTrackSender('video');
        }
    }
    /** @internal */
    restoreOutboundVideo() {
        if (this.peer && this.active) {
            this.peer.restoreTrackSender('video');
        }
    }
    /** @internal */
    setState(state) {
        this.prevState = this.state;
        this.state = state;
        this.logger.trace(`Call ${this.id} state change from ${this.prevState} to ${this.state}`);
        this.emit(this.state, this);
        switch (state) {
            case 'purge': {
                this._finalize();
                break;
            }
            case 'hangup': {
                this.setState('destroy');
                break;
            }
            case 'destroy':
                this._finalize();
                break;
        }
    }
    /** @internal */
    updateMediaOptions(options) {
        this.logger.debug('updateMediaOptions', Object.assign({}, options));
        this.options = Object.assign(Object.assign({}, this.options), options);
        this._checkDefaultMediaConstraints();
    }
    /**
     * Allow to define logic to munge the SDP
     *
     * @internal
     * */
    _mungeSDP(sdp) {
        return sdpRemoveLocalCandidates(sdp);
    }
    /**
     * Always use VIDEO_CONSTRAINTS if video: true
     * Always use AUDIO_CONSTRAINTS (or the SS one) if audio: true
     *
     * @internal
     */
    _checkDefaultMediaConstraints() {
        if (this.options.video === true) {
            this.options.video = VIDEO_CONSTRAINTS;
        }
        if (this.options.audio === true) {
            this.options.audio = this.options.screenShare
                ? AUDIO_CONSTRAINTS_SCREENSHARE
                : AUDIO_CONSTRAINTS;
        }
    }
    _initPeer() {
        // Build only for answer to be able to reject
        if (this.options.remoteSdp) {
            this.peer = this._buildPeer('answer');
        }
    }
    _buildPeer(type) {
        const rtcPeer = new RTCPeer(this, type);
        this.appendRTCPeer(rtcPeer);
        this.runRTCPeerWorkers(rtcPeer.uuid);
        return rtcPeer;
    }
    /** @internal */
    _finalize() {
        this.rtcPeerMap.forEach((rtcPeer) => {
            rtcPeer.stop();
        });
        this.rtcPeerMap.clear();
    }
    /**
     * Add or update the transceiver based on the media type and direction
     */
    _upsertTransceiverByKind(direction, kind) {
        if (!this.peer) {
            return this.logger.error('Invalid RTCPeerConnection');
        }
        let transceiver = this.peer.instance
            .getTransceivers()
            .find((tr) => { var _a, _b; return ((_a = tr.sender.track) === null || _a === void 0 ? void 0 : _a.kind) === kind || ((_b = tr.receiver.track) === null || _b === void 0 ? void 0 : _b.kind) === kind; });
        if (transceiver) {
            if (transceiver.direction === direction) {
                this.logger.info(`Transceiver ${kind} has the same direction "${direction}".`);
                return;
            }
            transceiver.direction = direction;
            this.logger.info(`Updated ${kind} transceiver to "${direction}" mode.`);
        }
        else {
            // No transceiver exists; add one if the direction is not "inactive"
            if (direction !== 'inactive') {
                // Ensure we act as the offerer when adding the transceiver during renegotiation
                this.peer.type = 'offer';
                transceiver = this.peer.instance.addTransceiver(kind, { direction });
                this.logger.info(`Added ${kind} transceiver in "${direction}" mode.`);
            }
        }
        if (direction === 'stopped' || direction === 'inactive') {
            this.peer.stopTrackReceiver(kind);
            this.peer.stopTrackSender(kind);
        }
        else if (direction === 'sendonly') {
            this.peer.stopTrackReceiver(kind);
        }
        else if (direction === 'recvonly') {
            this.peer.stopTrackSender(kind);
        }
    }
    /**
     * Allow user to upgrade/downgrade media in a call.
     * This performs RTC Peer renegotiation.
     *
     * @param params: {@link UpdateMediaParams}
     */
    async updateMedia(params) {
        var _a, _b, _c, _d, _e, _f;
        try {
            const { audio, video } = params;
            if (!this.peer) {
                throw new Error('Invalid RTCPeerConnection');
            }
            // Check if the peer is already negotiating
            if (this.peer.isNegotiating) {
                throw new Error('The peer is already negotiating the media!');
            }
            /**
             * Create a new renegotiation promise that would be resolved by the {@link executeUpdateMedia}
             */
            const peer = this.peer;
            const negotiationPromise = new Promise((resolve, reject) => {
                peer._pendingNegotiationPromise = {
                    resolve,
                    reject,
                };
            });
            const shouldEnableAudio = ['sendonly', 'sendrecv'].includes((audio === null || audio === void 0 ? void 0 : audio.direction) || '');
            const shouldEnableVideo = ['sendonly', 'sendrecv'].includes((video === null || video === void 0 ? void 0 : video.direction) || '');
            const shouldNegotiateAudio = ['sendrecv', 'receive'].includes((audio === null || audio === void 0 ? void 0 : audio.direction) || '');
            const shouldNegotiateVideo = ['sendrecv', 'receive'].includes((video === null || video === void 0 ? void 0 : video.direction) || '');
            this.updateMediaOptions(Object.assign(Object.assign(Object.assign(Object.assign({}, (audio && { audio: (_a = audio === null || audio === void 0 ? void 0 : audio.constraints) !== null && _a !== void 0 ? _a : shouldEnableAudio })), (video && { video: (_b = video === null || video === void 0 ? void 0 : video.constraints) !== null && _b !== void 0 ? _b : shouldEnableVideo })), (audio && { negotiateAudio: shouldNegotiateAudio })), (video && { negotiateVideo: shouldNegotiateVideo })));
            /**
             * The {@link applyConstraintsAndRefreshStream} updates the constraints,
             * gets the new user stream and updates the transceiver.
             * However, it only handles the media which is being enabled.
             * If the media is being disabled, we need to handle that below.
             */
            await this.applyConstraintsAndRefreshStream(Object.assign(Object.assign({}, (audio && { audio: (_c = audio === null || audio === void 0 ? void 0 : audio.constraints) !== null && _c !== void 0 ? _c : shouldEnableAudio })), (video && { video: (_d = video === null || video === void 0 ? void 0 : video.constraints) !== null && _d !== void 0 ? _d : shouldEnableVideo })));
            // When disabling audio
            if (audio && !shouldEnableAudio) {
                this._upsertTransceiverByKind(audio.direction, 'audio');
            }
            // When disabling video
            if (video && !shouldEnableVideo) {
                this._upsertTransceiverByKind(video.direction, 'video');
            }
            /**
             * Trigger the negotiation with the new settings.
             * If the negotiation is already ongoing it would not have a side effect.
             */
            await this.peer.startNegotiation();
            // Wait for the Renegotiation to complete
            await negotiationPromise;
            // Throw error if the remote SDP does not include the expected audio direction
            if (!hasMatchingSdpDirection({
                localSdp: this.peer.localSdp,
                remoteSdp: this.peer.remoteSdp,
                media: 'audio',
            })) {
                throw new Error('The server did not set the audio direction correctly');
            }
            // Throw error if the remote SDP does not include the expected video direction
            if (!hasMatchingSdpDirection({
                localSdp: this.peer.localSdp,
                remoteSdp: this.peer.remoteSdp,
                media: 'video',
            })) {
                throw new Error('The server did not set the video direction correctly');
            }
        }
        catch (error) {
            // Reject the negotiation promise if an error occurs
            (_f = (_e = this.peer) === null || _e === void 0 ? void 0 : _e._pendingNegotiationPromise) === null || _f === void 0 ? void 0 : _f.reject(error);
            throw error;
        }
    }
    /**
     * Allow user to set the audio direction on the RTC Peer.
     * This performs RTC Peer renegotiation.
     *
     * @param direction {@link UpdateMediaDirection}
     */
    async setAudioDirection(direction) {
        if (!['sendonly', 'sendrecv', 'recvonly', 'inactive'].includes(direction)) {
            throw new Error('Invalid direction specified');
        }
        return this.updateMedia({
            audio: { direction },
        });
    }
    /**
     * Allow user to set the video direction on the RTC Peer.
     * This performs RTC Peer renegotiation.
     *
     * @param direction {@link UpdateMediaDirection}
     */
    async setVideoDirection(direction) {
        if (!['sendonly', 'sendrecv', 'recvonly', 'inactive'].includes(direction)) {
            throw new Error('Invalid direction specified');
        }
        return this.updateMedia({
            video: { direction },
        });
    }
    async hold() {
        const message = VertoModify(Object.assign(Object.assign({}, this.dialogParams(this.callId)), { action: 'hold' }));
        await this.vertoExecute({
            message,
            callID: this.callId,
            node_id: this.nodeId,
        });
    }
    async unhold() {
        const message = VertoModify(Object.assign(Object.assign({}, this.dialogParams(this.callId)), { action: 'unhold' }));
        await this.vertoExecute({
            message,
            callID: this.callId,
            node_id: this.nodeId,
        });
    }
}
//# sourceMappingURL=BaseConnection.js.map